import React, { useState, useEffect } from "react";
import {
  useNavigate,
  Routes,
  Route,
  NavLink,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";
import "../styles/Dashboard.css";
import Home from "./Home";
import Part5_1 from "./questionCards/Part5/Part5_1";
import Part5_2 from "./questionCards/Part5/Part5_2";
import ListeningSection from "./sections/ListeningSection";
import ReadingSection from "./sections/ReadingSection";
import Part1_1 from "./questionCards/Part1/Part1_1";
import Part2_1 from "./questionCards/Part2/Part2_1";
import Part3_1 from "./questionCards/Part3/Part3_1";
import Part4_1 from "./questionCards/Part4/Part4_1";
import Part5Selection from "./questionCards/Part5/Part5Selection";
import Part5_Grammar from "./questionCards/Part5/Part5_Grammar";
import GrammarCategories from "./questionCards/Part5/GrammarCategories";
import Part6Selection from "./questionCards/Part6/Part6Selection";
import Part7Selection from "./questionCards/Part7/Part7Selection";
import PartsOfSpeechCategories from "./questionCards/Part5/PartsOfSpeechCategories";
import Part5_DirectReading from "./questionCards/Part5/Part5_DirectReading";
import Part5Vocabulary from "./questionCards/Part5/Part5Vocabulary";
import Account from "./Account";
import SubscriptionModal from "./SubscriptionModal";
import PaymentSuccessModal from "./PaymentSuccessModal";
import { useSubscriptionStatus } from "../hooks/useSubscriptionStatus";
import BlankTrainingExercise from "./questionCards/Part5/BlankTrainingExercise";
import BlankTrainingCategories from "./questionCards/Part5/BlankTrainingCategories";
import DirectListening from "./questionCards/Listening/DirectListening";

// 단순화된 메뉴 구조
const menuItems = {
  Listening: {
    "LC 직청직해": "Direct Listening",
    "Part 1": "Photo Description",
    "Part 2": "Question & Response",
    "Part 3": "Conversations",
    "Part 4": "Talks",
  },
  Reading: {
    "Part 5": "Part 5",
    "Part 6 & 7": "Text Completion",
  },
};

// 단순화된 NestedMenuItem 컴포넌트
const NestedMenuItem = ({ items, basePath, onMenuClick }) => {
  const location = useLocation();

  return (
    <ul className="nested-menu">
      {Object.entries(items).map(([key, value]) => {
        // 현재 URL에 해당 메뉴의 키워드가 포함되어 있는지 확인
        let isActive = false;

        // Part 6 & 7 메뉴 항목에 대한 특별 처리
        if (key === "Part 6 & 7") {
          isActive =
            location.pathname.toLowerCase().includes("part6") ||
            location.pathname.toLowerCase().includes("part7");
        } else {
          // 다른 메뉴 항목은 기존 로직 유지
          isActive =
            location.pathname
              .toLowerCase()
              .includes(key.toLowerCase().replace(/\s+/g, "")) ||
            location.pathname
              .toLowerCase()
              .includes(value.toLowerCase().replace(/\s+/g, ""));
        }

        return (
          <li key={key}>
            <NavLink
              to={`${basePath}/${key.replace(" & ", "")}`}
              className={`menu-link ${isActive ? "active" : ""}`}
              onClick={() => onMenuClick(key, value)}
            >
              {key}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
};

// URL 경로에 따라 메뉴 활성화 상태를 결정하는 함수 - 더 유연하게 수정
const getActiveMenuFromPath = (path) => {
  path = path.toLowerCase();

  if (path.includes("directlistening")) {
    return "Direct Listening";
  } else if (path.includes("part1") || path.includes("photo")) {
    return "Photo Description";
  } else if (path.includes("part2") || path.includes("question")) {
    return "Question & Response";
  } else if (path.includes("part3") || path.includes("conversation")) {
    return "Conversations";
  } else if (path.includes("part4") || path.includes("talk")) {
    return "Talks";
  } else if (
    path.includes("part5") ||
    path.includes("grammar") ||
    path.includes("vocabulary")
  ) {
    return "Part 5";
  } else if (
    path.includes("part6") ||
    path.includes("part7") ||
    path.includes("text")
  ) {
    return "Text Completion";
  }

  return "";
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // URL 파라미터 확인
  const paymentStatus = searchParams.get("payment");
  const userId = searchParams.get("user_id");
  const packageType = searchParams.get("package");

  const { isActive } = useSubscriptionStatus();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // URL이 변경될 때마다 selectedSubCategory 업데이트
  useEffect(() => {
    // URL 변경 시 메뉴 상태 업데이트
    const path = location.pathname.toLowerCase();

    // 메뉴 항목과 URL 매핑
    if (path.includes("part1")) {
      setSelectedSubCategory("Photo Description");
    } else if (path.includes("part2")) {
      setSelectedSubCategory("Question & Response");
    } else if (path.includes("part3")) {
      setSelectedSubCategory("Conversations");
    } else if (path.includes("part4")) {
      setSelectedSubCategory("Talks");
    } else if (path.includes("part5")) {
      setSelectedSubCategory("Part 5");
    } else if (path.includes("part6") || path.includes("part7")) {
      setSelectedSubCategory("Text Completion");
    }
  }, [location.pathname]);

  const [questions, setQuestions] = useState([]);
  // 초기값을 URL 기반으로 설정
  const [selectedSubCategory, setSelectedSubCategory] = useState(() => {
    return getActiveMenuFromPath(location.pathname);
  });
  const [selectedPart5Category, setSelectedPart5Category] = useState("");
  const [selectedGrammarType, setSelectedGrammarType] = useState(null);
  const [selectedPartOfSpeechType, setSelectedPartOfSpeechType] =
    useState(null);
  const [selectedBlankTrainingType, setSelectedBlankTrainingType] =
    useState(null);

  // Part1 컴포넌트가 보여질 때마다 새로운 키 값을 부여해서 강제로 재마운트시키는 변수
  const [part1Key, setPart1Key] = useState(0);

  const handleMenuClick = (category, subcategory) => {
    if (category === "홈") {
      setSelectedSubCategory("");
      navigate("/dashboard");
      return;
    }

    // Part 1 메뉴 클릭 시 항상 키 값을 변경하여 컴포넌트 강제 재마운트
    if (subcategory === "Photo Description") {
      setPart1Key((prev) => prev + 1);
    }

    setSelectedSubCategory(subcategory);

    if (subcategory === "Direct Listening") {
      navigate("/dashboard/listening/directlistening");
    } else if (subcategory === "Photo Description") {
      navigate("/dashboard/listening/Part1");
    } else if (subcategory === "Question & Response") {
      navigate("/dashboard/listening/Part2");
    } else if (subcategory === "Conversations") {
      navigate("/dashboard/listening/Part3");
    } else if (subcategory === "Talks") {
      navigate("/dashboard/listening/Part4");
    } else if (subcategory === "Part 5") {
      navigate("/dashboard/reading/Part5");
    } else if (subcategory === "Text Completion") {
      navigate("/dashboard/reading/Part6");
    }
  };

  const handlePart5CategorySelect = (category) => {
    setSelectedPart5Category(category);
  };

  const handleBack = () => {
    if (selectedGrammarType) {
      setSelectedGrammarType(null);
    } else if (selectedPart5Category) {
      setSelectedPart5Category("");
    } else if (selectedSubCategory) {
      setSelectedSubCategory("");
    }
  };

  const renderQuestionComponent = () => {
    if (!questions.length) return null;

    if (selectedSubCategory === "Photo Description") {
      return <Part1_1 isActive={isActive} />;
    } else if (selectedSubCategory === "Question & Response") {
      return <Part2_1 isActive={isActive} />;
    } else if (selectedSubCategory === "Conversations") {
      return <Part3_1 />;
    } else if (selectedSubCategory === "Talks") {
      return <Part4_1 />;
    } else if (selectedSubCategory === "Part 5") {
      return <Part5Selection isActive={isActive} />;
    }
    return null;
  };

  const renderContent = () => {
    if (location.pathname === "/dashboard/account") {
      return <Account />;
    }

    if (location.pathname === "/dashboard") {
      return <Home />;
    }

    // 명시적인 컴포넌트 매핑
    switch (selectedSubCategory) {
      case "Direct Listening":
        return <DirectListening isActive={isActive} />;
      case "Photo Description":
        return <Part1_1 key={part1Key} isActive={isActive} />;
      case "Question & Response":
        return <Part2_1 isActive={isActive} />;
      case "Conversations":
        return <Part3_1 isActive={isActive} />;
      case "Talks":
        return <Part4_1 isActive={isActive} />;
      case "Part 5":
        if (selectedPart5Category === "grammar") {
          if (selectedGrammarType) {
            return (
              <Part5_Grammar
                grammarType={selectedGrammarType}
                onBack={() => setSelectedGrammarType(null)}
              />
            );
          }
          return (
            <GrammarCategories
              onCategorySelect={setSelectedGrammarType}
              onBack={() => setSelectedPart5Category("")}
            />
          );
        } else if (selectedPart5Category === "parts-of-speech") {
          if (selectedPartOfSpeechType) {
            return (
              <Part5_DirectReading
                onBack={() => setSelectedPartOfSpeechType(null)}
              />
            );
          }
          return (
            <PartsOfSpeechCategories
              onCategorySelect={setSelectedPartOfSpeechType}
              onBack={() => setSelectedPart5Category("")}
            />
          );
        } else if (selectedPart5Category === "blank-training") {
          if (selectedBlankTrainingType) {
            return (
              <BlankTrainingExercise
                patternId={selectedBlankTrainingType}
                onBack={() => setSelectedBlankTrainingType(null)}
              />
            );
          }
          return (
            <BlankTrainingCategories
              onCategorySelect={setSelectedBlankTrainingType}
              onBack={() => setSelectedPart5Category("")}
            />
          );
        } else if (selectedPart5Category === "vocabulary") {
          return (
            <Part5Vocabulary onBack={() => setSelectedPart5Category("")} />
          );
        }
        return (
          <Part5Selection
            isActive={isActive}
            onCategorySelect={handlePart5CategorySelect}
            onBack={() => setSelectedSubCategory("")}
          />
        );
      case "Text Completion":
        return (
          <Part6Selection
            isActive={isActive}
            onCategorySelect={(category) => {}}
            onBack={() => setSelectedSubCategory("")}
          />
        );
      case "Part 6":
        return <Part6Selection isActive={isActive} />;
      default:
        return <Home />;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // PaymentSuccessModal 닫기 핸들러
  const handleClosePaymentSuccess = () => {
    // URL에서 파라미터 제거하고 기본 대시보드 URL로 변경
    window.history.replaceState({}, "", "/dashboard");
  };

  // subscription-status 부분 수정
  const renderSubscriptionBadge = () => {
    if (isActive) return null;

    return (
      <div className="subscription-status">
        <div
          className="subscription-badge"
          onClick={() => setIsSubscriptionModalOpen(true)}
          style={{
            cursor: "pointer",
            backgroundColor: "#FFD700",
            color: "#000000",
            transition: "all 0.3s ease",
            padding: "8px 16px",
            borderRadius: "20px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            margin: "0 10px",
          }}
        >
          <img
            src="/icons/crown.svg"
            alt="subscription"
            className="subscription-icon"
            style={{
              width: "20px",
              height: "20px",
              filter: "brightness(0)", // 아이콘을 검은색으로 변경
            }}
          />
          <span>Pro 이용하기</span>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <div className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-toggle">
          <img
            src="/icons/menu.svg"
            alt="toggle menu"
            className="menu-toggle-icon"
            onClick={toggleSidebar}
          />
        </div>
        <div className="sidebar-logo">
          <h1 className="logo-text">토익몬</h1>
        </div>
        <ul className="menu-list">
          <li className="menu-item">
            <NavLink
              to="/dashboard"
              end
              className={({ isActive }) =>
                `menu-link ${isActive ? "active" : ""}`
              }
              onClick={() => setSelectedSubCategory("")}
            >
              Home
            </NavLink>
          </li>
          <li className="menu-item">
            <span className="menu-link">Listening</span>
            <NestedMenuItem
              items={menuItems.Listening}
              basePath="/dashboard/listening"
              onMenuClick={handleMenuClick}
            />
          </li>
          <li className="menu-item">
            <span className="menu-link">Reading</span>
            <NestedMenuItem
              items={menuItems.Reading}
              basePath="/dashboard/reading"
              onMenuClick={handleMenuClick}
            />
          </li>
        </ul>
        {renderSubscriptionBadge()}
        <div className="account-section">
          <NavLink
            to="/dashboard/account"
            className={({ isActive }) =>
              `menu-link account-link ${isActive ? "active" : ""}`
            }
          >
            <div className="menu-link-content">
              <img
                src="/icons/account.svg"
                alt="settings"
                className="menu-icon"
              />
              <span>My Account</span>
            </div>
          </NavLink>
        </div>
      </div>
      <main className="main-content">{renderContent()}</main>

      {/* SubscriptionModal 조건부 렌더링 수정 */}
      {!isActive && (
        <SubscriptionModal
          isOpen={isSubscriptionModalOpen}
          onClose={() => setIsSubscriptionModalOpen(false)}
        />
      )}

      {/* 결제 성공 모달 - payment=success 파라미터가 있을 때만 표시 */}
      {paymentStatus === "success" && (
        <PaymentSuccessModal
          userId={userId}
          packageType={packageType}
          onClose={handleClosePaymentSuccess}
        />
      )}
    </div>
  );
};

export default DashboardPage;
